import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { IDS } from "@/constants/testIds";
import { OWNER } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full bg-transparent border border-border px-4 py-3 font-jet text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#E3FF00] focus:ring-2 focus:ring-[#E3FF00]/30 transition";

export function ConnectDialog({
  triggerTestId,
  triggerLabel,
  triggerClassName,
  source = "unknown",
}) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("email");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setDone(null);
    setForm({ name: "", email: "", phone: "", company: "", message: "" });
    setChannel("email");
  };

  // Email flow — POST only, no mailto redirect. Success screen stays in-page.
  const submitEmail = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, channel: "email", source };
      const res = await axios.post(`${API}/connect`, payload);
      setDone(res.data);
      toast.success("Details received. Bilal will reply within 9h.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // WhatsApp flow — one-tap: capture minimal info in background & jump to WA chat
  const openWhatsApp = async (prefilledMessage) => {
    setSubmitting(true);
    try {
      const payload = {
        name: form.name?.trim() || "WhatsApp visitor",
        email: form.email?.trim() || "unknown@wa.chat",
        phone: form.phone?.trim() || null,
        company: form.company?.trim() || null,
        message: prefilledMessage || form.message?.trim() || "Started a WhatsApp chat from portfolio",
        channel: "whatsapp",
        source,
      };
      // Fire-and-forget log; don't block the WA redirect on it
      axios.post(`${API}/connect`, payload).catch(() => {});
    } finally {
      setSubmitting(false);
    }
    const waText = encodeURIComponent(
      prefilledMessage ||
        (form.message?.trim()
          ? form.message.trim()
          : `Hi Bilal 👋, I found you via your portfolio and want to chat about a project.`)
    );
    window.open(`https://wa.me/${OWNER.whatsapp}?text=${waText}`, "_blank");
    toast.success("Opening WhatsApp…");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button
          data-testid={triggerTestId || IDS.connect.openTrigger}
          className={triggerClassName}
          type="button"
        >
          {triggerLabel}
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid={IDS.connect.dialog}
        className="max-w-lg p-0 gap-0 border border-[#F4F4F0] bg-[#141414] text-foreground rounded-none shadow-[10px_10px_0_0_#E3FF00]"
      >
        {done ? (
          <div className="p-8" data-testid={IDS.connect.success}>
            <div className="font-jet text-[10px] uppercase tracking-widest text-[#E3FF00]">Received</div>
            <div className="font-mono-brut text-3xl font-black uppercase tracking-tighter mt-2">
              You&apos;re in.
            </div>
            <p className="mt-4 text-sm text-foreground/70 leading-relaxed">
              Logged as <span className="text-[#E3FF00] font-jet">{done.id.slice(0, 8)}</span>.
              {done.channel === "whatsapp"
                ? " Opening WhatsApp in a new tab…"
                : " I'll reach out on the email you shared. Meanwhile you can also reach me at:"}
            </p>
            <div className="mt-4 space-y-2 font-jet text-sm">
              <div><span className="text-muted-foreground">Email:</span> {OWNER.email}</div>
              <div><span className="text-muted-foreground">WhatsApp:</span> {OWNER.whatsappDisplay}</div>
            </div>
            <button
              type="button"
              onClick={() => { reset(); setOpen(false); }}
              className="mt-6 inline-flex items-center gap-2 bg-[#E3FF00] text-[#0A0A0A] px-4 py-2 font-mono-brut text-xs uppercase tracking-widest"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="mb-6">
              <div className="font-jet text-[10px] uppercase tracking-widest text-[#E3FF00]">
                Let&apos;s build
              </div>
              <h3 className="font-mono-brut font-black uppercase text-2xl sm:text-3xl tracking-tighter mt-1">
                Get in touch.
              </h3>
            </div>

            {/* Channel selector */}
            <div className="grid grid-cols-2 gap-0 mb-6 border border-border">
              <button
                data-testid={IDS.connect.channelEmail}
                type="button"
                onClick={() => setChannel("email")}
                className={`flex items-center justify-center gap-2 py-3 font-mono-brut text-xs uppercase tracking-widest transition ${
                  channel === "email" ? "bg-[#E3FF00] text-[#0A0A0A]" : "hover:bg-[#1F1F1F]"
                }`}
              >
                <Mail size={14} /> Email
              </button>
              <button
                data-testid={IDS.connect.channelWhatsapp}
                type="button"
                onClick={() => setChannel("whatsapp")}
                className={`flex items-center justify-center gap-2 py-3 font-mono-brut text-xs uppercase tracking-widest border-l border-border transition ${
                  channel === "whatsapp" ? "bg-[#25D366] text-[#0A0A0A]" : "hover:bg-[#1F1F1F]"
                }`}
              >
                <MessageCircle size={14} /> WhatsApp
              </button>
            </div>

            {channel === "email" ? (
              <form onSubmit={submitEmail}>
                <div className="space-y-3">
                  <input
                    data-testid={IDS.connect.name}
                    required
                    placeholder="Your name *"
                    value={form.name}
                    onChange={upd("name")}
                    className={inputBase}
                  />
                  <input
                    data-testid={IDS.connect.email}
                    required
                    type="email"
                    placeholder="Email address *"
                    value={form.email}
                    onChange={upd("email")}
                    className={inputBase}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      data-testid={IDS.connect.phone}
                      placeholder="Phone (optional)"
                      value={form.phone}
                      onChange={upd("phone")}
                      className={inputBase}
                    />
                    <input
                      data-testid={IDS.connect.company}
                      placeholder="Company (optional)"
                      value={form.company}
                      onChange={upd("company")}
                      className={inputBase}
                    />
                  </div>
                  <textarea
                    data-testid={IDS.connect.message}
                    placeholder="What are you building? (optional)"
                    value={form.message}
                    onChange={upd("message")}
                    rows={4}
                    className={inputBase + " resize-none"}
                  />
                </div>

                <button
                  data-testid={IDS.connect.submit}
                  type="submit"
                  disabled={submitting}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E3FF00] text-[#0A0A0A] px-6 py-4 font-mono-brut font-black uppercase text-sm tracking-widest disabled:opacity-50 hover:brightness-105 transition"
                >
                  {submitting ? "Sending…" : (<>Send details <Send size={14} /></>)}
                </button>

                <div className="mt-3 text-center">
                  <a
                    data-testid="connect-mailto-fallback"
                    href={`mailto:${OWNER.email}`}
                    className="font-jet text-[11px] uppercase tracking-widest text-[#F4F4F0] underline underline-offset-4 hover:text-[#E3FF00] transition"
                  >
                    Send email instead?
                  </a>
                </div>
                <p className="mt-3 font-jet text-[10px] uppercase tracking-widest text-muted-foreground text-center">
                  Your details are stored securely so I can follow up.
                </p>
              </form>
            ) : (
              <div>
                {/* WhatsApp direct-jump UI */}
                <div className="border border-[#25D366]/40 bg-[#25D366]/5 p-5 mb-5">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-10 h-10 bg-[#25D366] text-[#0A0A0A] flex items-center justify-center">
                      <MessageCircle size={20} />
                    </div>
                    <div>
                      <div className="font-mono-brut font-black uppercase text-base">
                        Start a WhatsApp chat
                      </div>
                      <div className="font-jet text-[11px] text-muted-foreground mt-1">
                        Opens a new chat with {OWNER.whatsappDisplay}
                      </div>
                    </div>
                  </div>
                </div>

                <label className="font-jet text-[10px] uppercase tracking-widest text-muted-foreground">
                  Optional — prefill your first message
                </label>
                <textarea
                  data-testid={IDS.connect.message}
                  placeholder="Hi Bilal, I'd like to chat about…"
                  value={form.message}
                  onChange={upd("message")}
                  rows={3}
                  className={inputBase + " resize-none mt-2"}
                />

                <button
                  data-testid={IDS.connect.submit}
                  type="button"
                  onClick={() => openWhatsApp()}
                  disabled={submitting}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-[#0A0A0A] px-6 py-4 font-mono-brut font-black uppercase text-sm tracking-widest disabled:opacity-50 hover:brightness-105 transition"
                >
                  Open WhatsApp <ArrowUpRight size={14} />
                </button>
                <p className="mt-3 font-jet text-[10px] uppercase tracking-widest text-muted-foreground text-center">
                  A new chat with Bilal will open in a fresh tab.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
